/** Importar programa semanal desde foto del cuadro Excel — IA extrae y crea programaciones. */
import React, { useRef, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import { X, Camera, UploadSimple, Sparkle } from '@phosphor-icons/react';

const INP = 'w-full h-8 text-[11px] border border-[var(--border)] rounded bg-[var(--bg)] text-[var(--text)] px-1.5';

export default function ImportarProgramaFoto({ business, weekStart, onClose, onDone }) {
  const [img, setImg] = useState(null);
  const [mime, setMime] = useState('');
  const [loading, setLoading] = useState(false);
  const [creando, setCreando] = useState(false);
  const [items, setItems] = useState(null);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const leer = (f) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => { setImg(r.result); setMime(f.type || ''); setItems(null); };
    r.readAsDataURL(f);
  };

  const analizar = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/programacion/importar-foto', {
        imagen_base64: img, business_id: business.id, week_start: weekStart, mime,
      });
      const arr = (data.items || []).map((it) => ({ ...it, sel: !it.duplicado }));
      setItems(arr);
      const dups = arr.filter((it) => it.duplicado).length;
      if (!arr.length) toast.error('No se detectaron asignaciones en la imagen — intenta con una foto más clara');
      else if (dups) toast.warning(`${arr.length} asignaciones detectadas — ${dups} ya existen en la programación (desmarcadas)`);
      else toast.success(`${arr.length} asignaciones detectadas — revisa y corrige antes de crear`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error analizando la imagen'); }
    finally { setLoading(false); }
  };

  const setIt = (i, patch) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, ...patch } : it)));

  const crear = async () => {
    const sel = items.filter((it) => it.sel);
    if (!sel.length) return toast.error('Selecciona al menos una fila');
    setCreando(true);
    let ok = 0, err = 0, dup = 0;
    for (const it of sel) {
      try {
        await api.post('/programacion', {
          business_id: business.id,
          ot_numero: it.ot_numero || null,
          cliente_nombre: it.cliente_nombre || it.direccion || 'Por definir',
          direccion: it.direccion || '',
          descripcion_trabajo: it.descripcion_trabajo || '',
          instaladores: it.instaladores || [],
          equipo_personas: (it.instaladores || []).length || 1,
          fecha_inicio: it.fecha_inicio,
          fecha_fin: it.fecha_fin,
        });
        ok += 1;
      } catch (e) { if (e?.response?.status === 409) dup += 1; else err += 1; }
    }
    setCreando(false);
    if (ok) toast.success(`${ok} programaciones creadas${dup ? ` · ${dup} duplicadas (ya existían)` : ''}${err ? ` · ${err} fallaron` : ''}`);
    else if (dup) toast.warning(`Todas ya existían (${dup} duplicadas) — no se creó ninguna`);
    else toast.error('No se pudo crear ninguna programación');
    if (ok) { onDone(); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" data-testid="importar-programa-modal">
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black text-[var(--text)]">📷 Importar programa desde foto</h3>
          <button onClick={onClose} className="p-1 hover:bg-[var(--surface-muted)] rounded" data-testid="importar-cerrar"><X size={18} /></button>
        </div>
        <p className="text-[11px] text-[var(--text-muted)]">Sube la foto o captura de tu cuadro de Excel (filas = cuadrilla, columnas = días). La IA lo lee, te muestra un borrador para revisar y al confirmar se crean las programaciones de la semana del <b>{weekStart}</b>.</p>

        <div className="flex gap-2 flex-wrap">
          <button onClick={() => fileRef.current?.click()} className="px-3 py-2 border border-[var(--border)] rounded-md text-xs font-bold text-[var(--text)] hover:bg-[var(--surface-muted)] flex items-center gap-1.5" data-testid="importar-subir">
            <UploadSimple size={14} /> Subir archivo
          </button>
          <button onClick={() => camRef.current?.click()} className="px-3 py-2 border border-[var(--border)] rounded-md text-xs font-bold text-[var(--text)] hover:bg-[var(--surface-muted)] flex items-center gap-1.5" data-testid="importar-camara">
            <Camera size={14} /> Tomar foto
          </button>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => leer(e.target.files?.[0])} data-testid="importar-file-input" />
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => leer(e.target.files?.[0])} />
          {img && !items && (
            <button onClick={analizar} disabled={loading} className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs font-bold flex items-center gap-1.5 disabled:opacity-60" data-testid="importar-analizar">
              <Sparkle size={14} /> {loading ? 'Leyendo el cuadro…' : 'Analizar con IA'}
            </button>
          )}
        </div>

        {img && !items && !mime.includes('pdf') && (
          <img src={img} alt="cuadro" className="max-h-56 rounded-md border border-[var(--border)] object-contain" data-testid="importar-preview" />
        )}

        {items && items.length > 0 && (
          <>
            <div className="border border-[var(--border)] rounded-md overflow-x-auto">
              <table className="w-full text-[11px] min-w-[820px]">
                <thead>
                  <tr className="text-[9px] uppercase font-black text-[var(--text-muted)] bg-[var(--surface-muted)]">
                    <th className="px-2 py-1.5"><input type="checkbox" checked={items.every((it) => it.sel)} onChange={(e) => setItems((arr) => arr.map((it) => ({ ...it, sel: e.target.checked })))} data-testid="importar-sel-todos" /></th>
                    <th className="px-2 py-1.5 text-left">Cuadrilla</th>
                    <th className="px-2 py-1.5 text-left">OT</th>
                    <th className="px-2 py-1.5 text-left">Trabajo</th>
                    <th className="px-2 py-1.5 text-left">Ubicación</th>
                    <th className="px-2 py-1.5 text-left">Inicio</th>
                    <th className="px-2 py-1.5 text-left">Fin</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className={`border-t border-[var(--border)] ${it.sel ? '' : 'opacity-40'}`} data-testid={`importar-fila-${i}`}>
                      <td className="px-2 py-1 text-center"><input type="checkbox" checked={it.sel} onChange={(e) => setIt(i, { sel: e.target.checked })} data-testid={`importar-sel-${i}`} /></td>
                      <td className="px-2 py-1 min-w-[130px]"><input className={INP} value={it.cuadrilla} onChange={(e) => setIt(i, { cuadrilla: e.target.value, instaladores: e.target.value.split(/[/,+]/).map((n, j) => ({ nombre: n.trim(), es_responsable: j === 0 })).filter((x) => x.nombre) })} /></td>
                      <td className="px-2 py-1 w-[70px]">
                        <input className={INP} value={it.ot_numero} onChange={(e) => setIt(i, { ot_numero: e.target.value })} data-testid={`importar-ot-${i}`} />
                        {it.duplicado && <span className="text-[8px] font-black text-amber-600 whitespace-nowrap" data-testid={`importar-dup-${i}`}>⚠ Ya existe</span>}
                      </td>
                      <td className="px-2 py-1 min-w-[190px]"><input className={INP} value={it.descripcion_trabajo} onChange={(e) => setIt(i, { descripcion_trabajo: e.target.value })} /></td>
                      <td className="px-2 py-1 min-w-[110px]"><input className={INP} value={it.direccion} onChange={(e) => setIt(i, { direccion: e.target.value, cliente_nombre: e.target.value })} /></td>
                      <td className="px-2 py-1 w-[115px]"><input type="date" className={INP} value={it.fecha_inicio} onChange={(e) => setIt(i, { fecha_inicio: e.target.value })} /></td>
                      <td className="px-2 py-1 w-[115px]"><input type="date" className={INP} value={it.fecha_fin} onChange={(e) => setIt(i, { fecha_fin: e.target.value })} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 border border-[var(--border)] rounded-md text-xs font-bold text-[var(--text-muted)]">Cancelar</button>
              <button onClick={crear} disabled={creando} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs font-bold disabled:opacity-60" data-testid="importar-crear">
                {creando ? 'Creando…' : `Crear ${items.filter((it) => it.sel).length} programaciones`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
